import { createSelector } from '@ngrx/store';

import { TransactionStage } from 'src/generated/graphql.generated';

import { blueSageRefundInProgress } from '../bluesage/bluesage.util';
import { zonesFeature } from '../core/zones/zones.reducer';
import { canBeInvoiced } from '../invoices/invoices.utils';
import { jobFranchiseOf, selectJob } from '../job/job-tool.reducer';
import { jobsFeature } from '../jobs/jobs.reducer';
import { currentTimeSeconds } from '../time';

import { createTransactionDialogFeature } from './create-transaction-dialog/create-transaction-dialog.feature';

export const selectTransactionJob = createSelector(
  selectJob,
  jobsFeature.selectJobs,
  createTransactionDialogFeature.selectJobId,
  (routeJob, jobs, dialogJobId) => routeJob ?? (dialogJobId ? jobs[dialogJobId] : undefined),
);

const selectTransactionJobInCurrentZone = createSelector(
  selectTransactionJob,
  zonesFeature.selectCurrentAppZone,
  (job, currentZone) => {
    const franchiseId = jobFranchiseOf(job)?.id;

    if (!franchiseId || !currentZone) { return true; }

    return franchiseId === currentZone.id;
  },
);

// Requirement: read live, not snapshotted at dialog-open time — a hold can be
// completed or released elsewhere while the dialog stays open, and acting on
// a stale one moves money against the wrong authorization.
export const selectJobCollectibles = createSelector(
  selectTransactionJob,
  selectTransactionJobInCurrentZone,
  (job, jobInCurrentZone) => {
    if (!job) { return undefined; }

    const transactions = job.transactions ?? [];
    const [ latestInvoice ] = [ ...(job.invoices ?? []) ].sort((a, b) => b.createdAt - a.createdAt);

    const customerId = job.users?.find((u) => u.role === 'customer')?.user?.id;

    // Requirement: Complete appears only on a transaction already holding an
    // open authorization — money HELD, never taken, so the transaction is
    // still `pending`.
    // Requirement: a hold whose RELEASE is still being confirmed is not offered
    // for completion — a void and a capture must never race on one reader. The
    // server refuses it too; this keeps the wizard from offering what it would.
    const openPreauths = transactions.filter((t) =>
      !t.deletedAt
      && t.stage === TransactionStage.Pending
      && Boolean(t.metadata?.bluesagePreauthRef)
      && !blueSageRefundInProgress(transactions, t.id));

    // Requirement: with more than one open hold, no job-wide Complete shortcut
    // is offered — the amounts differ, and settling the wrong one leaves the
    // other frozen on the customer's card. The row menu is the only path
    // then, since it knows which transaction it's on.
    const openPreauth = openPreauths.length === 1 ? openPreauths[0] : undefined;

    return {
      jobId: job.id,
      customerId,
      currency: job.currency,
      balanceMinor: Math.max(0, (job.total ?? 0) - (job.paidTotal ?? 0)),
      depositMinor: job.depositRequired ?? 0,
      openPreauthTransactionId: openPreauth?.id,
      // Requirement: every open hold, not just the unambiguous one — the row
      // menu needs the full list live to validate the hold it's acting on,
      // independent of the single-hold shortcut above.
      openPreauths: openPreauths.map((t) => ({
        id: t.id,
        epi: t.metadata?.bluesageTerminalEpi as string | undefined,
        amountMinor: Math.abs(t.amount ?? 0),
      })),
      openPreauthCount: openPreauths.length,
      // Requirement: the EPI is the terminal the hold was actually taken on,
      // not whichever reader the picker last used — completion can only go to
      // that terminal, since the middleware overwrites the EPI with the
      // auth's own.
      openPreauthEpi: openPreauth?.metadata?.bluesageTerminalEpi,
      openPreauthAmountMinor: Math.abs(openPreauth?.amount ?? 0),
      // Requirement: `transactions.create` refuses a payment with no invoice
      // linked, and a voided or deleted invoice is not one to post against —
      // so neither counts here.
      invoiceId: latestInvoice && !latestInvoice.voidedAt && !latestInvoice.deletedAt
        ? latestInvoice.id
        : undefined,
      // Requirement: every invoice still owing something, not just the
      // latest — a voided, deleted, or fully-paid invoice does not count as
      // open.
      hasAnyInvoices: (job.invoices ?? []).some((invoice) => !invoice.voidedAt && !invoice.deletedAt),
      openInvoices: (job.invoices ?? [])
        .filter((invoice) => !invoice.voidedAt
          && !invoice.deletedAt
          && ((invoice.total ?? 0) - (invoice.paidTotal ?? 0)) > 0)
        .map((invoice) => ({
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          dueDate: invoice.dueDate,
          balanceMinor: Math.max(0, (invoice.total ?? 0) - (invoice.paidTotal ?? 0)),
          createdAt: invoice.createdAt,
        }))
        // Requirement: oldest first — the deposit invoice precedes the final
        // balance, and that's the order an operator expects to read them in.
        .sort((a, b) => a.createdAt - b.createdAt),
      invoiceableEvents: (job.events ?? [])
        .filter((event) => !event.deletedAt && canBeInvoiced(event, currentTimeSeconds()))
        .map((event) => ({
          id: event.id,
          title: event.title,
          start: event.start,
          totalMinor: event.total ?? 0,
        }))
        .sort((a, b) => a.start - b.start),
      enabled: jobInCurrentZone && Boolean(customerId),
    };
  },
);
